import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import Flatpickr from "react-flatpickr";

const optionValues = (customFieldValue) => {
  return (customFieldValue.custom_field.custom_field_options || [])
    .filter((option) => !option._destroy)
    .sort((a, b) => (a.position || 0) - (b.position || 0));
};

const GenericCustomFieldValue = ({
  customFieldValue,
  customFieldValueChanged,
  inline=false,
  idSuffix=null,
  restrictCheckoutFiles=true
}) => {
  const type = customFieldValue.type;
  const options = optionValues(customFieldValue);
  const fileInputRef = useRef(null);

  const onValueChange = (value) => {
    customFieldValueChanged({
      ...customFieldValue,
      value
    });
  };

  const fieldLabel = customFieldValue.custom_field.name
    + (!customFieldValue.custom_field.required ? " (optional)" : "");

  const renderInput = () => {
    if (type === 'CustomFieldValues::Select') {
      return (
        <select
          className="form-control"
          style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
          value={customFieldValue.value || ""}
          onChange={(e) => {
            onValueChange(e.target.value);
          }}
        >
          <option value="">{fieldLabel}</option>
          {options.map((option) => (
            <option key={option.id || option.name} value={option.name}>
              {option.name}
            </option>
          ))}
        </select>
      );
    }

    if (type === 'CustomFieldValues::Checkbox') {
      const checkboxId = `custom-field-checkbox-${customFieldValue.custom_field.id}${idSuffix ? `-${idSuffix}` : ''}`;

      return (
        <div className="custom-control custom-switch" style={{"paddingLeft": "42px"}}>
          <input
            id={checkboxId}
            type="checkbox"
            className="custom-control-input"
            checked={customFieldValue.value === true || customFieldValue.value === "true"}
            onChange={(e) => {
              onValueChange(e.target.checked ? "true" : "false");
            }}
          />
          <label
            htmlFor={checkboxId}
            className="custom-control-label"
          >
            <span style={{"fontSize": "14px"}}>{customFieldValue.custom_field.name}{!customFieldValue.custom_field.required ? " (optional)" : ""}</span>
          </label>
        </div>
      );
    }

    if (type === 'CustomFieldValues::File') {
      const fileName = customFieldValue.uploaded_file_name || customFieldValue.value;

      return (
        <div
          className="form-control d-flex align-items-center"
          style={{
            "border": "1px solid var(--od-c-e6e6e6, #e6e6e6)",
            "fontSize": "14px",
            "cursor": "pointer",
            "padding": "0",
            "overflow": "hidden"
          }}
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
        >
          <span
            style={{
              "backgroundColor": "var(--od-c-e9ecef, #e9ecef)",
              "borderRadius": "4px",
              "padding": "4px 12px",
              "margin": "4px 4px 4px 4px",
              "fontSize": "13px",
              "color": "var(--od-c-495057, #495057)",
              "whiteSpace": "nowrap",
              "flexShrink": "0"
            }}
          >
            Choose File
          </span>
          <span
            style={{
              "padding": "6px 12px 6px 0",
              "color": fileName ? "var(--od-c-495057, #495057)" : "var(--od-c-999999, #999)",
              "overflow": "hidden",
              "textOverflow": "ellipsis",
              "whiteSpace": "nowrap",
              "flex": "1"
            }}
          >
            {fileName || fieldLabel}
          </span>
          {fileName ? (
            <span
              style={{
                "padding": "0 10px",
                "color": "var(--od-c-999999, #999)",
                "cursor": "pointer",
                "fontSize": "16px",
                "flexShrink": "0"
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (fileInputRef.current) fileInputRef.current.value = "";
                customFieldValueChanged({
                  ...customFieldValue,
                  uploaded_file: null,
                  uploaded_file_name: "",
                  value: ""
                });
              }}
            >
              &times;
            </span>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept={restrictCheckoutFiles ? ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" : undefined}
            style={{"display": "none"}}
            onChange={(e) => {
              const uploadedFile = e.target.files && e.target.files[0];
              if (restrictCheckoutFiles && uploadedFile && uploadedFile.size > 10 * 1024 * 1024) {
                e.target.value = "";
                window.alert("Files must be 10 MB or smaller.");
                return;
              }

              customFieldValueChanged({
                ...customFieldValue,
                uploaded_file: uploadedFile,
                uploaded_file_name: uploadedFile ? uploadedFile.name : customFieldValue.uploaded_file_name,
                value: uploadedFile ? uploadedFile.name : customFieldValue.value
              });
            }}
          />
        </div>
      );
    }

    if (type === 'CustomFieldValues::Date') {
      return (
        <Flatpickr
          className="form-control"
          placeholder={fieldLabel}
          value={customFieldValue.value || ""}
          options={{
            dateFormat: "Y-m-d",
            altInput: true,
            altFormat: "F j, Y",
            altInputClass: "form-control"
          }}
          onChange={([date]) => {
            if (date) {
              const y = date.getFullYear();
              const m = String(date.getMonth() + 1).padStart(2, '0');
              const d = String(date.getDate()).padStart(2, '0');
              onValueChange(`${y}-${m}-${d}`);
            } else {
              onValueChange("");
            }
          }}
        />
      );
    }

    if (type === 'CustomFieldValues::Time') {
      return (
        <Flatpickr
          className="form-control"
          placeholder={fieldLabel}
          value={customFieldValue.value || ""}
          options={{
            enableTime: true,
            noCalendar: true,
            dateFormat: "H:i",
            altInput: true,
            altFormat: "h:i K",
            time_24hr: false,
            altInputClass: "form-control"
          }}
          onChange={([date]) => {
            if (date) {
              const h = String(date.getHours()).padStart(2, '0');
              const min = String(date.getMinutes()).padStart(2, '0');
              onValueChange(`${h}:${min}`);
            } else {
              onValueChange("");
            }
          }}
        />
      );
    }

    if (type === 'CustomFieldValues::Timestamp') {
      return (
        <Flatpickr
          className="form-control"
          placeholder={fieldLabel}
          value={customFieldValue.value || ""}
          options={{
            enableTime: true,
            dateFormat: "Y-m-d H:i",
            altInput: true,
            altFormat: "F j, Y - h:i K",
            time_24hr: false,
            altInputClass: "form-control"
          }}
          onChange={([date]) => {
            if (date) {
              const y = date.getFullYear();
              const m = String(date.getMonth() + 1).padStart(2, '0');
              const d = String(date.getDate()).padStart(2, '0');
              const h = String(date.getHours()).padStart(2, '0');
              const min = String(date.getMinutes()).padStart(2, '0');
              onValueChange(`${y}-${m}-${d} ${h}:${min}`);
            } else {
              onValueChange("");
            }
          }}
        />
      );
    }

    const typeToInputType = {
      'CustomFieldValues::Email': 'email',
      'CustomFieldValues::Url': 'url',
      'CustomFieldValues::Number': 'number'
    };

    const inputType = typeToInputType[type] || 'text';

    return (
      <input
        type={inputType}
        className="form-control"
        style={{"border": "1px solid var(--od-c-e6e6e6, #e6e6e6)", "fontSize": "14px"}}
        placeholder={fieldLabel}
        maxLength={customFieldValue.custom_field.max_length || undefined}
        value={customFieldValue.value || ""}
        onChange={(e) => {
          onValueChange(e.target.value);
        }}
      />
    );
  };

  if (inline) {
    return (
      <div className="col-6 mt-3">
        {renderInput()}
      </div>
    );
  }

  const isCheckbox = type === 'CustomFieldValues::Checkbox';

  if (isCheckbox) {
    return (
      <div className="row mt-3" style={{"padding": "10px 0 15px 0"}}>
        <div className="col-12">
          {renderInput()}
          {customFieldValue.custom_field.help_text ? (
            <p className="small mb-2" style={{ color: 'var(--sl-text-tertiary)', marginTop: '-8px' }}>
              {customFieldValue.custom_field.help_text}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="row mt-3" style={{"padding": "10px 0 15px 0"}}>
      <div className="col-12">
        <div className="row">
          <div className="col-12">
            <p style={{
                 "fontSize": "16px",
                 "marginBottom": "12px"
               }}>
              <strong>{customFieldValue.custom_field.name}</strong>{!customFieldValue.custom_field.required ? " (optional)" : null}
            </p>
            {customFieldValue.custom_field.help_text ? (
              <p className="small mb-2" style={{ color: 'var(--sl-text-tertiary)', marginTop: '-8px' }}>
                {customFieldValue.custom_field.help_text}
              </p>
            ) : null}
          </div>
        </div>
        <div className="form-row">
          <div className="col-12">
            {renderInput()}
          </div>
        </div>
      </div>
    </div>
  );
};

GenericCustomFieldValue.propTypes = {
  customFieldValue: PropTypes.object.isRequired,
  customFieldValueChanged: PropTypes.func.isRequired,
  inline: PropTypes.bool,
  idSuffix: PropTypes.string,
  restrictCheckoutFiles: PropTypes.bool
};

export default GenericCustomFieldValue;
